/* public-i18n-migrated */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, ArrowUpRight, Flame, Loader2, MapPin, Calendar, Clock, Users, Check, Languages, Star, Camera, X, CreditCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AllRounderLogo from "@/components/AllRounderLogo";
import { useI18n } from "@/i18n";
import { localizedBbqEvent } from '@/lib/bbqLocale';
import { BookingAction } from '@/booking/BookingAction';
import { useBookingConsent } from '@/booking/useBookingConsent';
import { ConsentHistory } from '@/booking/ConsentHistory';
import { useHostedCheckout } from '@/booking/useHostedCheckout';
import { HostedCheckoutNotice } from '@/booking/HostedCheckoutNotice';

const BBQ_LANGS = [
  { code: "ko", label: "KR" },
  { code: "en", label: "EN" },
  { code: "zh", label: "CN" },
  { code: "ja", label: "JP" },
  { code: "es", label: "ES" },
  { code: "pt", label: "PT" },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG =
  "https://images.unsplash.com/photo-1621851709622-e19c9a4f0cc5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const IMG_SKEWERS =
  "https://images.unsplash.com/photo-1534797258760-1bd2cc95a5bd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const IMG_PARTY =
  "https://images.unsplash.com/photo-1517456793572-1d8efd6dc135?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const DEFAULT_PRICES = { bbq_only: 30, bbq_beer: 32 };

const HERO_LINES = ["All Friends", "BBQ Party."];

const HOUSE_RULES = [
  {
    num: "01",
    title: "First come, first served",
    body:
      "Registration is strictly limited to 50 attendees. Once the fire's full, it's full — reserve early to lock your seat by the grill.",
    ko: "선착순 마감 · 최대 50명",
  },
  {
    num: "02",
    title: "Bring your student ID",
    body:
      "Registered under the student rate? Bring a valid student ID on the night. It keeps the discount honest and the crew happy.",
    ko: "학생 요금 등록 시 학생증 지참",
  },
  {
    num: "03",
    title: "Everything is on us",
    body:
      "All meat, vegetables, seasonings, sauces, soft drinks and BBQ equipment will be ready on site. Just show up hungry.",
    ko: "고기·채소·양념·장비 모두 준비 완료",
  },
];

// --- Kinetic hero title with masked line-by-line reveal ---
function KineticTitle() {
  return (
    <h1
      data-testid="bbq-hero-title"
      className="mt-6 font-display leading-[0.86] tracking-tighter text-white"
      style={{
        fontFamily: "'Fraunces', 'Playfair Display', serif",
        fontWeight: 700,
        fontSize: "clamp(60px, 15vw, 168px)",
      }}
    >
      {HERO_LINES.map((line, i) => (
        <span key={i} className="block overflow-hidden">
          <motion.span
            className="block will-change-transform"
            initial={{ y: "110%" }}
            animate={{ y: "0%" }}
            transition={{
              duration: 1.2,
              delay: 0.2 + i * 0.18,
              ease: [0.19, 1, 0.22, 1],
            }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
}

// --- Reveal wrapper ---
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-15% 0px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, delay, ease: [0.19, 1, 0.22, 1] }}
    >
      {children}
    </motion.div>
  );
}

// --- Marquee ---
function Marquee({ title }) {
  const { tx } = useI18n();

  return (
    <div
      data-testid="bbq-marquee"
      className="relative border-y border-white/10 bg-[#0A0806] py-4 sm:py-6 overflow-hidden"
    >
      <motion.div
        className="flex whitespace-nowrap gap-10 will-change-transform"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 26, ease: "linear", repeat: Infinity }}
      >
        {Array.from({ length: 14 }).map((_, i) => (
          <span
            key={i}
            className="text-white/85 font-display text-2xl sm:text-4xl font-bold tracking-tight uppercase flex items-center gap-6"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            {title || "South Bank BBQ Party"}
            <span className="text-[#E85A2C]">·</span>
            <span className="text-[#E85A2C]">{tx("RSVP Now")}</span>
            <span className="text-[#E85A2C]">·</span>
            <span className="text-white/60">All Friends × All Rounder</span>
            <span className="text-[#E85A2C]">·</span>
          </span>
        ))}
      </motion.div>
    </div>
  );
}

function GalleryCarousel({ images }) {
  const { tx } = useI18n();

  const list = images || [];

  // Group by round (empty round => "기본")
  const rounds = useMemo(() => {
    const arr = images || [];
    const map = new Map();
    for (const it of arr) {
      const key = (it.round || "").trim() || "기본";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    return Array.from(map.entries()).map(([name, items]) => ({ name, items }));
  }, [images]);

  const [roundIdx, setRoundIdx] = useState(0);
  const [idx, setIdx] = useState(0);
  const activeRound = rounds[roundIdx] || rounds[0];
  const roundImages = activeRound ? activeRound.items : [];

  useEffect(() => { setIdx(0); }, [roundIdx]);
  useEffect(() => {
    if (roundImages.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % roundImages.length), 3500);
    return () => clearInterval(t);
  }, [roundImages.length, roundIdx]);

  if (list.length === 0) return null;

  return (
    <section
      data-testid="bbq-gallery-section"
      className="relative border-t border-white/5 bg-[#0F0B08]"
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-10 py-20 sm:py-28">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]"> {tx("// Gallery")} </p>
          <h2
            className="mt-4 text-white text-4xl sm:text-6xl font-bold tracking-tight leading-[0.95]"
            style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
          > {tx("Moments.")} </h2>
        </Reveal>

        {/* Round tabs */}
        {rounds.length > 1 && (
          <Reveal delay={0.1} className="mt-6">
            <div className="flex flex-wrap gap-2" data-testid="bbq-gallery-rounds">
              {rounds.map((r, i) => (
                <button
                  key={r.name}
                  type="button"
                  onClick={() => setRoundIdx(i)}
                  data-testid={`bbq-gallery-round-${i}`}
                  className={`px-4 py-2 rounded-full text-[11px] uppercase tracking-widest font-bold transition-colors ${
                    i === roundIdx ? "bg-[#E85A2C] text-white" : "bg-white/5 text-white/50 hover:text-white"
                  }`}
                >
                  {r.name === '기본' ? tx('Gallery') : r.name}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        <Reveal delay={0.15} className="mt-8">
          <div className="relative aspect-[16/10] w-full rounded-md overflow-hidden border border-white/15 bg-black">
            <AnimatePresence mode="wait">
              <motion.img
                key={`${roundIdx}-${idx}`}
                src={(roundImages[idx] || {}).image}
                alt={`Gallery ${idx + 1}`}
                data-testid={`bbq-gallery-slide-${idx}`}
                className="absolute inset-0 w-full h-full object-cover"
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
              />
            </AnimatePresence>
            {roundImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                {roundImages.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={tx("Go to slide {p0}", {p0: i + 1})}
                    data-testid={`bbq-gallery-dot-${i}`}
                    onClick={() => setIdx(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === idx ? "w-6 bg-[#E85A2C]" : "w-1.5 bg-white/40 hover:bg-white/70"
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}


function DetailCell({ eyebrow, value, subtitle, Icon }) {
  return (
    <div className="flex items-start gap-4 py-6 border-b border-white/10 last:border-b-0">
      <span className="mt-1 shrink-0 w-9 h-9 rounded-sm bg-[#E85A2C]/10 text-[#E85A2C] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">
          {eyebrow}
        </div>
        <div
          className="mt-1 text-white text-2xl sm:text-3xl font-bold tracking-tight leading-tight"
          style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
        >
          {value}
        </div>
        {subtitle && (
          <div className="text-white/50 text-xs mt-1">{subtitle}</div>
        )}
      </div>
    </div>
  );
}

// --- Registration form ---
function RsvpForm({ event, paymentEmergency, bank }) {
  const { tx, errorText } = useI18n();

  const [mode, setMode] = useState("solo");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    social: "",
    status: "general",
    package: "bbq_only",
    group_size: 1,
    discount_code: "",
  });
  const [groupForm, setGroupForm] = useState({
    name: "",
    email: "",
    phone: "",
    group_size: 10,
    note: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [pendingPayId, setPendingPayId] = useState(null);
  const [bookingKey] = useState(()=>crypto.randomUUID());
  const [bookingIdentity,setBookingIdentity] = useState(null);
  const mediaConsent=useBookingConsent('bbq','current',`new:${bookingKey}`);
  const retryConsent=useBookingConsent('bbq','current',`retry:${pendingPayId}`,Boolean(pendingPayId));
  const hostedCheckout=useHostedCheckout(mediaConsent);
  const retryCheckout=useHostedCheckout(retryConsent);
  const [pricing, setPricing] = useState({
    prices: DEFAULT_PRICES,
    group_discounts: [0, 0, 1, 2, 2, 2, 2, 2, 2, 2],
    promo_codes: [],
  });

  useEffect(() => {
    axios.get(`${API}/bbq-pricing`).then((r) => setPricing(r.data)).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment") === "cancelled") {
      const id = sessionStorage.getItem("bbq_pending_rsvp");
      if (id) setPendingPayId(id);
    }
  }, []);

  // Group discount: dynamic per-pax from admin settings
  const discountPerPax = Number(pricing.group_discounts?.[form.group_size]) || 0;
  const basePricePerPax = Number(pricing.prices?.[form.package] ?? DEFAULT_PRICES[form.package]);
  const groupDiscountedPerPax = Math.max(basePricePerPax - discountPerPax, 0);

  // Promo code — ONLY valid when solo (group_size === 1) and matches any active code
  const codeTrimmed = (form.discount_code || "").trim().toLowerCase();
  const matchedPromo = (pricing.promo_codes || []).find((p) => p.code?.toLowerCase() === codeTrimmed);
  const codeValid = !!matchedPromo && form.group_size === 1;
  const codeConflicts = codeTrimmed && form.group_size > 1;

  const promoDiscount = codeValid ? Number(matchedPromo.amount || 0) : 0;
  const finalPerPax = Math.max(groupDiscountedPerPax - promoDiscount, 0);
  const total = finalPerPax * form.group_size;
  const pricePerPax = finalPerPax; // rename for existing UI

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setG = (k, v) => setGroupForm((p) => ({ ...p, [k]: v }));

  const submitSolo = async (e) => {
    e.preventDefault();
    if(!mediaConsent.submission){toast.error(tx('Photography and publicity consent is required before booking or payment.'));return;}
    if (!form.name || !form.email || !form.phone) {
      toast.error(tx("Please fill in all required fields"));
      return;
    }
    if (codeConflicts) {
      toast.error(tx("Discount code doesn't stack"), {
        description: tx("Remove the code or set group size to 1."),
      });
      return;
    }
    if (codeTrimmed && !codeValid && form.group_size === 1) {
      toast.error(tx("Invalid discount code"));
      return;
    }
    setLoading(true);
    const ticket=!paymentEmergency&&total>0?hostedCheckout.prepare():null;
    try {
      const r = await axios.post(`${API}/bbq-rsvps`, {
        ...form,
        discount_code: codeValid ? matchedPromo.code : null,
        total,
        media_consent: mediaConsent.submission,
      },{withCredentials:true,headers:{'X-Booking-Key':bookingKey}});
      setBookingIdentity({id:r.data.id,event_id:r.data.event_id});
      if (paymentEmergency) {
        setDone("bank");
        setLoading(false);
        toast.success(tx("Registration received"));
        return;
      }
      sessionStorage.setItem("bbq_pending_rsvp", r.data.id);
      const pay = await axios.post(`${API}/payments/checkout`, {
        rsvp_id: r.data.id,
        origin_url: window.location.origin,
        media_consent: mediaConsent.submission,
      },{withCredentials:true});
      if (pay.data.free) {
        hostedCheckout.cancel(ticket);
        sessionStorage.removeItem("bbq_pending_rsvp");
        setDone("solo");
        setLoading(false);
        toast.success(tx("You're confirmed 🔥"));
      } else {
        hostedCheckout.open(pay.data.checkout_url,ticket||hostedCheckout.prepare());
        setPendingPayId(r.data.id);
        setLoading(false);
      }
    } catch (err) {
      hostedCheckout.cancel(ticket);
      const detail = err?.response?.data?.detail || "Please try again";
      toast.error(tx("RSVP failed"), { description: errorText(detail) });
      mediaConsent.reload();
      setLoading(false);
    }
  };

  const retryPayment = async () => {
    if (!pendingPayId || !retryConsent.submission) return;
    setLoading(true);
    const ticket=retryCheckout.prepare();
    try {
      const pay = await axios.post(`${API}/payments/checkout`, {
        rsvp_id: pendingPayId,
        origin_url: window.location.origin,
        media_consent: retryConsent.submission,
      },{withCredentials:true});
      if (pay.data.free) {
        retryCheckout.cancel(ticket);
        sessionStorage.removeItem("bbq_pending_rsvp");
        setPendingPayId(null);
        setDone("solo");
        setLoading(false);
      } else {
        retryCheckout.open(pay.data.checkout_url,ticket);
        setLoading(false);
      }
    } catch (err) {
      retryCheckout.cancel(ticket);
      const detail = err?.response?.data?.detail || "Please try again";
      toast.error(tx("Payment failed to start"), { description: errorText(detail) });
      retryConsent.reload();
      sessionStorage.removeItem("bbq_pending_rsvp");
      setPendingPayId(null);
      setLoading(false);
    }
  };

  const submitGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.name || !groupForm.email || !groupForm.phone) {
      toast.error(tx("Please fill in all required fields"));
      return;
    }
    if (groupForm.group_size < 10) {
      toast.error(tx("Group inquiry requires 10+ people"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bbq-group-inquiries`, groupForm);
      setDone("group");
      toast.success(tx("Group inquiry received 🙌"), {
        description: tx("We'll be in touch within 24 hours."),
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Please try again";
      toast.error(tx("Inquiry failed"), { description: errorText(detail) });
    } finally {
      setLoading(false);
    }
  };

  if (done === "bank") {
    const amount = total;
    return (
      <div
        data-testid="bbq-rsvp-bank"
        className="rounded-md border border-yellow-500/40 bg-[#0A0806] p-6 sm:p-8"
      >
        <div className="flex items-center gap-3">
          <Flame className="w-6 h-6 text-yellow-400" />
          <span data-testid="bbq-bank-status" className="text-[10px] uppercase tracking-[0.28em] font-bold text-yellow-400">{tx("Registration received · Bank transfer")}</span>
        </div>
        <h3
          className="mt-4 text-white text-2xl sm:text-3xl font-bold tracking-tight leading-[1.1]"
          style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
          data-testid="bbq-bank-title"
        >{tx("Please pay by bank transfer")}</h3>
        <p data-testid="bbq-bank-description" className="mt-3 text-white/60 text-sm leading-relaxed">{tx("Card payment is temporarily unavailable. Transfer A${amount} to the account below. Your place will be confirmed after we verify payment.", {amount})}</p>

        <div className="mt-6 rounded-md border border-dashed border-yellow-500/40 bg-yellow-500/5 p-5">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-yellow-400/90">{tx("Bank transfer details")}</div>
          <div data-testid="bbq-bank-amount" className="mt-3 font-bold text-white text-3xl" style={{ fontFamily: "'Fraunces', serif" }}>
            ${amount} <span className="text-white/50 text-sm">AUD · {tx("{count} guests", {count:form.group_size})}</span>
          </div>
          <div className="mt-4 divide-y divide-white/10 text-sm">
            <div className="flex justify-between py-2">
              <span className="text-white/50">{tx("Account Name")}</span>
              <span className="text-white font-semibold" data-testid="bank-name">{bank?.name || "-"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-white/50">BSB</span>
              <span className="text-white font-semibold font-mono" data-testid="bank-bsb">{bank?.bsb || "-"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-white/50">{tx("Account No.")}</span>
              <span className="text-white font-semibold font-mono" data-testid="bank-account">{bank?.account || "-"}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-white/50">{tx("Reference")}</span>
              <span data-testid="bbq-bank-reference" className="text-white font-semibold font-mono break-all">BBQ {form.name}</span>
            </div>
          </div>
        </div>
        <p data-testid="bbq-bank-reference-notice" className="mt-4 text-white/40 text-xs leading-relaxed">{tx('Use "{reference}" as the transfer reference. A confirmation email will be sent after payment is verified.', {reference:`BBQ ${form.name}`})}</p>
        <ConsentHistory id="bbq-bank-consent" scope="bbq" eventId={bookingIdentity?.event_id} bookingId={bookingIdentity?.id}/>
      </div>
    );
  }

  if (done === "solo") {
    return (
      <div
        data-testid="bbq-rsvp-success"
        className="rounded-md border border-[#E85A2C]/40 bg-[#0A0806] p-6 sm:p-8"
      >
        <div className="flex items-center gap-3">
          <Flame className="w-6 h-6 text-[#E85A2C]" />
          <span className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C]"> {tx("Confirmed")} </span>
        </div>
        <h3
          className="mt-4 text-white text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05]"
          style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
        > {tx("You're on the list. 🔥")} </h3>
        <p data-testid="bbq-confirmation-email" className="mt-3 text-white/60 text-sm leading-relaxed">{tx("A confirmation email is on its way to {email}. Just bring yourself on the day!",{email:form.email})}</p>
        <ConsentHistory id="bbq-free-consent" scope="bbq" eventId={bookingIdentity?.event_id} bookingId={bookingIdentity?.id}/>

        <div className="mt-6 grid grid-cols-3 gap-3 text-center">
          <MiniStat label={tx("Date")} value={event?.short_date || "Fri, Aug 7"} />
          <MiniStat label={tx("Time")} value={event?.short_time || "5–8 PM"} />
          <MiniStat label={tx("Location")} value={event?.short_location || "South Bank"} />
        </div>
      </div>
    );
  }

  if (done === "group") {
    return (
      <div
        data-testid="bbq-group-success"
        className="rounded-md border border-[#E85A2C]/30 bg-[#0A0806] p-8 text-center"
      >
        <Flame className="w-8 h-8 text-[#E85A2C] mx-auto" />
        <div
          className="mt-4 text-white text-3xl font-bold tracking-tight"
          style={{ fontFamily: "'Fraunces', serif" }}
        > {tx("Inquiry received.")} </div>
        <p data-testid="bbq-group-confirmation" className="mt-3 text-white/60 text-sm leading-relaxed">{tx("We'll contact you at {phone} or {email} within 24 hours to arrange your booking for {count} guests.",{phone:groupForm.phone,email:groupForm.email,count:groupForm.group_size})}</p>
      </div>
    );
  }

  const packages = [
    { key: "bbq_only", title: tx("BBQ Only"), note: tx("Meat · sides · soft drinks · equipment") },
  ];

  const groupOptions = [
    { size: 1, label: "Just me", note: "Standard rate" },
    { size: 2, label: "2 of us", note: "-$1 each" },
    { size: 3, label: "3", note: "-$2 each" },
    { size: 4, label: "4", note: "-$2 each" },
    { size: 5, label: "5", note: "-$2 each" },
    { size: 6, label: "6", note: "-$2 each" },
    { size: 7, label: "7", note: "-$2 each" },
    { size: 8, label: "8", note: "-$2 each" },
    { size: 9, label: "9", note: "-$2 each" },
  ];

  return (
    <div>
      {/* Payment not completed — retry banner */}
      {pendingPayId && (
        <div
          data-testid="bbq-payment-retry"
          className="mb-6 p-4 rounded-sm border border-yellow-500/40 bg-yellow-500/10 flex flex-wrap items-center justify-between gap-3"
        >
          <div className="text-sm text-white/85 leading-relaxed">{tx("Your payment wasn't completed — your spot isn't locked yet.")}</div>
          <BookingAction consent={retryConsent} type="button" onClick={retryPayment} busy={loading} id="bbq-payment-retry-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} {tx("Complete payment")}
          </BookingAction>
          <HostedCheckoutNotice checkout={retryCheckout} id="bbq-retry"/>
        </div>
      )}

      {/* Mode switcher */}
      <div
        data-testid="bbq-mode-switch"
        className="grid grid-cols-2 gap-2 mb-8 p-1 rounded-sm border border-white/10 bg-black/20"
      >
        <button
          type="button"
          data-testid="bbq-mode-solo"
          onClick={() => setMode("solo")}
          className={`h-11 rounded-sm text-sm font-semibold transition-colors ${
            mode === "solo"
              ? "bg-[#E85A2C] text-white"
              : "text-white/60 hover:text-white"
          }`}
        > {tx("1 – 9 people")} </button>
        <button
          type="button"
          data-testid="bbq-mode-group"
          onClick={() => setMode("group")}
          className={`h-11 rounded-sm text-sm font-semibold transition-colors ${
            mode === "group"
              ? "bg-[#E85A2C] text-white"
              : "text-white/60 hover:text-white"
          }`}
        > {tx("10+ (Group inquiry)")} </button>
      </div>

      {mode === "solo" ? (
        <form
          onSubmit={submitSolo}
          data-testid="bbq-rsvp-form"
          noValidate
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <FormField label={tx("Full Name")} required>
            <Input
              data-testid="bbq-input-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder={tx("Kim Sungho")}
              className="h-11 rounded-sm bg-black/20 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent"
            />
          </FormField>
          <FormField label={tx("Email")} required>
            <Input
              data-testid="bbq-input-email"
              type="email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              placeholder="you@example.com"
              className="h-11 rounded-sm bg-black/20 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent"
            />
          </FormField>
          <FormField label={tx("Mobile Number")} required>
            <Input
              data-testid="bbq-input-phone"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+61 4 0000 0000"
              className="h-11 rounded-sm bg-black/20 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent"
            />
          </FormField>
          <FormField label={tx("KakaoTalk / Instagram")}>
            <Input
              data-testid="bbq-input-social"
              value={form.social}
              onChange={(e) => set("social", e.target.value)}
              placeholder="@allrounder_aus"
              className="h-11 rounded-sm bg-black/20 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent"
            />
          </FormField>

          <div className="sm:col-span-2 mt-2">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-2"> {tx("Package")} </div>
            <div className="grid grid-cols-1 gap-2">
              {packages.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`bbq-package-${p.key}`}
                  onClick={() => set("package", p.key)}
                  className={`text-left rounded-sm p-4 border transition-all ${
                    form.package === p.key
                      ? "border-[#E85A2C] bg-[#E85A2C]/10"
                      : "border-white/15 bg-black/20 hover:border-white/30"
                  }`}
                >
                  <div className="flex items-baseline justify-between">
                    <div className="text-white font-bold text-lg">{p.title}</div>
                    <div className="text-[#E85A2C] font-bold tabular">
                      ${pricing.prices?.[p.key] ?? DEFAULT_PRICES[p.key]}
                    </div>
                  </div>
                  <div className="text-white/50 text-xs mt-1">{p.note}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2 mt-2">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-2"> {tx("Group Size · Friends bring discounts")} </div>
            <div className="grid grid-cols-3 sm:grid-cols-9 gap-1.5">
              {groupOptions.map((g) => (
                <button
                  key={g.size}
                  type="button"
                  data-testid={`bbq-group-${g.size}`}
                  onClick={() => set("group_size", g.size)}
                  className={`rounded-sm p-2 border text-center transition-all ${
                    form.group_size === g.size
                      ? "border-[#E85A2C] bg-[#E85A2C]/10"
                      : "border-white/15 bg-black/20 hover:border-white/30"
                  }`}
                >
                  <div
                    className="text-white font-bold text-lg tabular"
                    style={{ fontFamily: "'Fraunces', serif" }}
                  >
                    {g.size}
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-white/50">
              {discountPerPax > 0
                ? tx("🎉 A${amount} off per person", {amount:discountPerPax})
                : tx("Standard rate")}
              {tx(" · Need 10+? Switch to Group inquiry above.")}
            </div>
          </div>

          {/* Discount code — only usable when solo (group_size = 1) */}
          <div className="sm:col-span-2 mt-2">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-2"> {tx("Discount Code · Optional")} </div>
            <div className="flex flex-col sm:flex-row gap-2 items-stretch">
              <Input
                data-testid="bbq-input-code"
                value={form.discount_code}
                onChange={(e) => set("discount_code", e.target.value)}
                placeholder={tx("Have a code? Enter it here")}
                disabled={form.group_size > 1}
                className={`h-11 rounded-sm bg-black/20 border text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent flex-1 ${
                  codeValid
                    ? "border-[#E85A2C]"
                    : codeConflicts || (codeTrimmed && !codeValid)
                    ? "border-red-500/50"
                    : "border-white/15"
                } ${form.group_size > 1 ? "opacity-50 cursor-not-allowed" : ""}`}
              />
              {codeValid && (
                <div
                  data-testid="bbq-code-applied"
                  className="h-11 px-4 rounded-sm bg-[#E85A2C]/15 text-[#E85A2C] font-bold text-sm flex items-center gap-1.5 border border-[#E85A2C]/40"
                >
                  <Check className="w-4 h-4" />
                  −${Number(matchedPromo?.amount || 0)} {tx("applied")} </div>
              )}
            </div>
            <div className="mt-2 text-[11px] text-white/50 leading-relaxed">
              {codeConflicts && (
                <span className="text-red-400"> {tx("⚠️ Codes can't be combined with the group discount. Choose one.")} </span>
              )}
              {!codeConflicts && codeTrimmed && !codeValid && (
                <span className="text-red-400"> {tx("⚠️ Invalid code.")} </span>
              )}
              {!codeTrimmed && (
                <span> {tx("Solo only (not combinable with the 2+ group discount).")} </span>
              )}
            </div>
          </div>

          {/* Student ID upload — removed per request */}

          <div className="sm:col-span-2 mt-4 flex flex-col items-stretch gap-4 p-5 rounded-sm border border-white/10 bg-[#0A0806]">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50"> {tx("Calculated Total")} </div>
              <div
                className="text-white text-3xl font-bold tabular"
                style={{ fontFamily: "'Fraunces', serif" }}
                data-testid="bbq-total"
              >
                ${total}.00
                <span className="text-white/40 text-sm font-normal ml-2">AUD · ${pricePerPax} × {form.group_size} · {paymentEmergency?tx("Bank transfer"):tx("Card payment")}</span>
              </div>
            </div>
            <BookingAction consent={mediaConsent} id="bbq-submit" busy={loading}
              valid={Boolean(!pendingPayId&&form.name.trim()&&form.phone.trim()&&/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)&&form.group_size>=1&&form.group_size<=9&&!codeConflicts&&(!codeTrimmed||codeValid))}>
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {paymentEmergency?tx("Submitting…"):tx("Redirecting to payment...")} </>
              ) : (
                <> {paymentEmergency?tx("Submit registration"):tx("RSVP & Pay")} <CreditCard className="w-4 h-4 ml-2" />
                </>
              )}
            </BookingAction>
            <HostedCheckoutNotice checkout={hostedCheckout} id="bbq"/>
          </div>
        </form>
      ) : (
        <form
          onSubmit={submitGroup}
          data-testid="bbq-group-form"
          noValidate
          className="grid grid-cols-1 sm:grid-cols-2 gap-4"
        >
          <div className="sm:col-span-2 rounded-sm border border-[#E85A2C]/30 bg-[#E85A2C]/5 p-4 text-sm text-white/80 leading-relaxed">
            <b className="text-white">{tx("Group of 10 or more?")}</b> {tx("Leave your contact and we'll design a custom quote (private pavilion, extra grills, bulk pricing). We reply within 24 hours.")} </div>

          <FormField label={tx("Full Name")} required>
            <Input
              data-testid="bbq-group-input-name"
              value={groupForm.name}
              onChange={(e) => setG("name", e.target.value)}
              placeholder={tx("Kim Sungho")}
              className="h-11 rounded-sm bg-black/20 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent"
            />
          </FormField>
          <FormField label={tx("Email")} required>
            <Input
              data-testid="bbq-group-input-email"
              type="email"
              value={groupForm.email}
              onChange={(e) => setG("email", e.target.value)}
              placeholder="you@example.com"
              className="h-11 rounded-sm bg-black/20 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent"
            />
          </FormField>
          <FormField label={tx("Mobile Number")} required>
            <Input
              data-testid="bbq-group-input-phone"
              value={groupForm.phone}
              onChange={(e) => setG("phone", e.target.value)}
              placeholder="+61 4 0000 0000"
              className="h-11 rounded-sm bg-black/20 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent"
            />
          </FormField>
          <FormField label={tx("Group Size (10+)")} required>
            <Input
              data-testid="bbq-group-input-size"
              type="number"
              min={10}
              value={groupForm.group_size}
              onChange={(e) =>
                setG("group_size", Math.max(10, parseInt(e.target.value || 10, 10)))
              }
              placeholder="10"
              className="h-11 rounded-sm bg-black/20 border-white/15 text-white placeholder:text-white/30 focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent"
            />
          </FormField>
          <div className="sm:col-span-2">
            <FormField label={tx("Note (optional)")}>
              <textarea
                data-testid="bbq-group-input-note"
                value={groupForm.note}
                onChange={(e) => setG("note", e.target.value)}
                placeholder={tx("Preferred date, dietary needs, company/team name, etc.")}
                rows={4}
                className="w-full rounded-sm bg-black/20 border border-white/15 text-white placeholder:text-white/30 px-3 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E85A2C] focus-visible:border-transparent resize-none"
              />
            </FormField>
          </div>

          <div className="sm:col-span-2 mt-2">
            <Button
              type="submit"
              data-testid="bbq-group-submit"
              disabled={loading}
              className="w-full h-12 px-8 rounded-sm bg-[#E85A2C] hover:bg-[#c94a20] text-white font-bold text-[15px]"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {tx("Sending...")} </>
              ) : (
                <> {tx("Send Group Inquiry")} <ArrowUpRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">
        {label}
        {required && <span className="text-[#E85A2C] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-sm border border-white/10 bg-black/20 p-3">
      <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-white/50">
        {label}
      </div>
      <div
        className="mt-1 text-white text-sm font-bold"
        style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
      >
        {value}
      </div>
    </div>
  );
}

function BbqLangToggle() {
  const { lang, setLang , tx} = useI18n();
  return (
    <div className="flex items-center gap-0.5 text-[10px] font-semibold">
      <Languages className="w-3.5 h-3.5 mr-1 text-white/50 shrink-0" aria-hidden />
      {BBQ_LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          data-testid={`bbq-lang-${l.code}`}
          onClick={() => setLang(l.code)}
          className={`px-1.5 py-1 rounded-md transition-colors min-w-[22px] ${
            lang === l.code
              ? "bg-[#E85A2C] text-white"
              : "text-white/50 hover:text-white"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

// --- Star rating (interactive or display) ---
function Stars({ value = 0, onChange, size = 20, testid }) {
  const { tx } = useI18n();

  const isInteractive = typeof onChange === "function";
  return (
    <div className={`flex items-center gap-1 ${isInteractive ? "cursor-pointer" : ""}`} data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= value;
        return (
          <button
            key={n}
            type="button"
            disabled={!isInteractive}
            onClick={() => isInteractive && onChange(n)}
            className={`${isInteractive ? "hover:scale-110" : "cursor-default"} transition-transform`}
            aria-label={tx("{p0} star", {p0: n})}
            data-testid={testid ? `${testid}-${n}` : undefined}
          >
            <Star
              className={active ? "text-[#E85A2C] fill-[#E85A2C]" : "text-white/20"}
              style={{ width: size, height: size }}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
}

// --- Review submission form ---
function ReviewForm({ onCreated }) {
  const { tx } = useI18n();

  const [form, setForm] = useState({ name: "", rating: 5, text: "" });
  const [photos, setPhotos] = useState([]); // base64 data URLs
  const [loading, setLoading] = useState(false);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    files.forEach((f) => {
      if (!f.type.startsWith("image/")) return;
      if (f.size > 2_000_000) {
        toast.error(tx("{name} is over 2MB", {name:f.name}));
        return;
      }
      const reader = new FileReader();
      reader.onload = () => setPhotos((prev) => (prev.length < 6 ? [...prev, reader.result] : prev));
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const removePhoto = (idx) => setPhotos((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.text.trim()) {
      toast.error(tx("Please fill in your name and message"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bbq-reviews`, { ...form, photos });
      toast.success(tx("Thanks! Your review is live 🔥"));
      setForm({ name: "", rating: 5, text: "" });
      setPhotos([]);
      onCreated && onCreated();
    } catch {
      toast.error(tx("Failed to submit review"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="bbq-review-form">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-2 block">{tx("Your Name")}</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder={tx("e.g. Minjae K.")}
            data-testid="review-input-name"
            className="w-full bg-black/40 border border-white/15 rounded-sm px-4 py-3 text-white outline-none focus:border-[#E85A2C]"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-2 block">{tx("Your Rating")}</label>
          <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={26} testid="review-stars" />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50 mb-2 block">{tx("Message")}</label>
        <textarea
          rows={4}
          value={form.text}
          onChange={(e) => setForm({ ...form, text: e.target.value })}
          placeholder={tx("Tell everyone what you loved…")}
          data-testid="review-input-text"
          className="w-full bg-black/40 border border-white/15 rounded-sm px-4 py-3 text-white outline-none focus:border-[#E85A2C] resize-none"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">{tx("Photos (optional · max 6)")}</label>
          <span className="text-[10px] text-white/40">{photos.length}/6</span>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {photos.map((p, i) => (
            <div key={i} className="relative aspect-square rounded-sm overflow-hidden border border-white/10 group">
              <img src={p} alt={`upload ${i + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(i)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label={tx("remove")}
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          ))}
          {photos.length < 6 && (
            <label
              className="aspect-square rounded-sm border border-dashed border-white/25 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-[#E85A2C]/50 transition-colors bg-black/20"
              data-testid="review-photo-upload"
            >
              <Camera className="w-5 h-5 text-white/50" />
              <span className="text-[10px] uppercase tracking-widest text-white/50">{tx("Add")}</span>
              <input type="file" accept="image/*" multiple onChange={onPickFiles} className="hidden" />
            </label>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="review-submit"
        className="w-full sm:w-auto px-8 py-3.5 rounded-sm bg-[#E85A2C] text-white font-bold text-sm uppercase tracking-widest hover:bg-[#f26a3c] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4 fill-white" />} {tx("Post review")} </button>
    </form>
  );
}

// --- Single review card (used inside train carousel) ---
function ReviewMiniCard({ r }) {
  const { tx, lang } = useI18n();

  const hasPhotos = r.photos?.length > 0;
  return (
    <div className="shrink-0 w-[320px] sm:w-[380px] h-[320px] sm:h-[340px] rounded-lg border border-white/20 bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur p-5 sm:p-6 shadow-xl flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="text-white font-bold text-lg sm:text-xl truncate" style={{ fontFamily: "'Fraunces', serif" }}>
            {r.name}
          </div>
          <div className="text-white/50 text-[10px] uppercase tracking-widest mt-0.5">
            {r.created_at ? new Date(r.created_at).toLocaleDateString(lang, { day: "numeric", month: "long" }) : ""}
          </div>
        </div>
        <Stars value={r.rating} size={14} />
      </div>
      <p className={`text-white/85 text-[13px] sm:text-sm leading-relaxed whitespace-pre-wrap overflow-hidden ${hasPhotos ? "line-clamp-2" : "line-clamp-6"} flex-1`}>
        {r.text}
      </p>
      {hasPhotos && (
        <div className="mt-3 relative aspect-video w-full rounded-md overflow-hidden border border-white/20 bg-black/30">
          <img src={r.photos[0]} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          {r.photos.length > 1 && (
            <div className="absolute bottom-2 right-2 px-2 py-1 rounded bg-black/70 backdrop-blur text-white text-[10px] font-bold uppercase tracking-widest">
              +{r.photos.length - 1} {tx("more")} </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Reviews carousel — train-style continuous horizontal marquee ---
function ReviewsCarousel({ reviews }) {
  const { tx } = useI18n();

  const [paused, setPaused] = useState(false);
  const n = reviews.length;
  if (n === 0) return null;
  // Duplicate the rail for seamless loop
  const rail = [...reviews, ...reviews];
  // Speed proportional to number of cards (~9 sec per card)
  const duration = Math.max(24, n * 9);
  return (
    <div
      className="relative overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="reviews-carousel"
    >
      {/* Edge fades */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-24 bg-gradient-to-r from-[#0F0B08] to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-24 bg-gradient-to-l from-[#0A0806] to-transparent z-10" />

      <div
        className="reviews-rail flex gap-4 sm:gap-5 py-2 will-change-transform"
        style={{
          animation: `reviews-marquee ${duration}s linear infinite`,
          animationPlayState: paused ? "paused" : "running",
        }}
      >
        {rail.map((r, i) => (
          <ReviewMiniCard key={`${r.id}-${i}`} r={r} />
        ))}
      </div>
      <style>{`
        @keyframes reviews-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

// --- Reviews section (used on /bbq) ---
function ReviewsSection() {
  const { t , tx} = useI18n();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/bbq-reviews`)
      .then((r) => setReviews(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section
      data-testid="bbq-reviews-section"
      id="reviews"
      className="relative border-t border-white/5 bg-gradient-to-b from-[#0F0B08] via-[#141010] to-[#0A0806]"
    >
      <div className="max-w-4xl mx-auto px-5 sm:px-10 py-20 sm:py-28">
        <Reveal>
          <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]"> {tx("// Event Reviews")} </p>
          <h2
            className="mt-4 text-white text-4xl sm:text-6xl font-bold tracking-tight leading-[0.95]"
            style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
          > {tx("Event Reviews")} </h2>
          <p className="mt-5 text-white/70 leading-relaxed max-w-xl">
            {tx("Share your experience — leave a rating, a message, and any photos from the night.")}
          </p>
        </Reveal>

        {/* Reviews carousel */}
        {reviews.length > 0 && (
          <Reveal delay={0.1} className="mt-12">
            <ReviewsCarousel reviews={reviews} />
          </Reveal>
        )}

        {/* Submission form */}
        <Reveal delay={0.15} className="mt-14">
          <div className="p-6 sm:p-8 rounded-lg border border-white/15 bg-white/[0.03] backdrop-blur">
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#E85A2C] mb-2">{tx("Leave your review")}</div>
            <div className="text-white text-xl sm:text-2xl font-bold mb-6" style={{ fontFamily: "'Fraunces', serif" }}> {tx("Tell everyone how it went")} </div>
            <ReviewForm onCreated={load} />
          </div>
        </Reveal>

        {loading && (
          <div className="text-center py-10 text-white/40 mt-6"><Loader2 className="w-5 h-5 animate-spin inline-block" /></div>
        )}
        {!loading && reviews.length === 0 && (
          <div className="text-center py-10 text-white/40 mt-6">{tx("Be the first to leave a review.")}</div>
        )}

        <div className="mt-14 text-center">
          <Link
            to="/bbq/reviews"
            data-testid="reviews-see-more"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-white/25 text-white hover:border-[#E85A2C] hover:text-[#E85A2C] transition-colors text-[11px] uppercase tracking-[0.28em] font-bold"
          > {tx("See all reviews & media")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}



export default function BbqPage() {
  const { t, tx, lang } = useI18n();
  const [count, setCount] = useState({ total: 0, capacity: 50 });
  const [pageActive, setPageActive] = useState(null); // null = loading
  const [paymentEmergency, setPaymentEmergency] = useState(false);
  const [bank, setBank] = useState({ name: "", bsb: "", account: "" });
  const [gallery, setGallery] = useState([]);
  const [media, setMedia] = useState({ hero_image: "", detail_image: "", video_id: "", video_updated_at: "", hero_bg_mode: "image" });
  const [eventSource, setEvent] = useState({
    title: "South Bank BBQ Party",
    eyebrow: "South Bank Social · Aug 7",
    date_label: "Fri, Aug 7",
    date_note: "Rain or shine — covered pavilion secured",
    time_label: "5:00 – 8:00 PM",
    time_note: "Arrive from 4:45 to help fire the grill",
    location_label: "South Bank Park",
    location_note: "Riverside pavilion · closest to the Wheel",
    location_area: "South Bank",
    location_region: "QLD · Australia",
    short_date: "Fri, Aug 7",
    short_time: "5–8 PM",
    short_location: "South Bank",
  });
  const event = localizedBbqEvent(eventSource, lang);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    document.title = "BBQ Party · All Rounder";
    axios
      .get(`${API}/bbq-rsvps/count`)
      .then((r) => setCount(r.data))
      .catch(() => {});
    axios
      .get(`${API}/bbq-event`)
      .then((r) => setEvent((prev) => ({ ...prev, ...r.data })))
      .catch(() => {});
    axios
      .get(`${API}/bbq-status`)
      .then((r) => {
        setPageActive(r.data.active !== false);
        setPaymentEmergency(!!r.data.payment_emergency);
      })
      .catch(() => {});
    axios.get(`${API}/bbq-bank`).then((r) => setBank(r.data)).catch(() => {});
    axios.get(`${API}/bbq-gallery`).then((r) => setGallery(r.data.items || [])).catch(() => {});
    axios
      .get(`${API}/bbq-media`)
      .then((r) => setMedia((prev) => ({ ...prev, ...r.data })))
      .catch(() => {});
  }, []);

  if (pageActive === null) {
    return (
      <main className="fixed inset-0 bg-[#0A0806] flex items-center justify-center" data-testid="bbq-loading-screen">
        <Loader2 className="w-6 h-6 animate-spin text-[#E85A2C]" />
      </main>
    );
  }

  if (pageActive === false) {
    return (
      <main
        data-testid="bbq-closed-screen"
        className="relative min-h-screen bg-[#0A0806] text-white overflow-x-clip"
        style={{ fontFamily: "'Noto Sans KR', 'Fraunces', serif" }}
      >
        <div className="fixed inset-0 bg-[#0A0806] -z-20" />
        <div className="absolute top-5 right-5 z-30"><BbqLangToggle /></div>
        {/* Waiting message (kept) */}
        <section className="relative flex flex-col items-center justify-center px-6 text-center pt-28 pb-20 sm:pt-36 sm:pb-28 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(232,90,44,0.12),transparent_60%)]" />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="relative z-10 max-w-lg"
          >
            <Flame className="w-12 h-12 text-[#E85A2C] mx-auto" />
            <h1
              className="mt-6 text-white text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 700 }}
              data-testid="bbq-closed-title"
            >{tx("The next event is on its way.")}</h1>
            <p data-testid="bbq-closed-description" className="mt-5 text-white/60 text-base leading-relaxed">{tx("The next BBQ party is being prepared — check back soon.")}</p>
            <p className="mt-6 text-white/40 text-xs uppercase tracking-[0.28em] font-bold"> {tx("지난 이벤트 둘러보기 ↓")} </p>
          </motion.div>
        </section>

        {/* Promo video (if uploaded) */}
        {media.video_id && (
          <section data-testid="bbq-closed-video" className="relative border-t border-white/5 bg-[#0F0B08]">
            <div className="max-w-5xl mx-auto px-5 sm:px-10 py-16 sm:py-24">
              <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]">{tx("// Promo Film")}</p>
              <h2 className="mt-4 text-white text-3xl sm:text-5xl font-bold tracking-tight leading-[0.95]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}> {tx("Feel the vibe.")} </h2>
              <div className="mt-8 rounded-md overflow-hidden border border-white/15 bg-black shadow-2xl">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  data-testid="bbq-closed-promo-video"
                  className="w-full aspect-video object-contain bg-black"
                  src={`${API}/bbq-video?v=${encodeURIComponent(media.video_updated_at || "")}`}
                />
              </div>
            </div>
          </section>
        )}

        {/* Moments gallery + reviews */}
        <GalleryCarousel images={gallery} />
        <ReviewsSection />

        <div className="border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6 py-14 text-center">
            <Link
              to="/"
              data-testid="bbq-closed-home-link"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-sm border border-white/25 text-white hover:border-[#E85A2C] hover:text-[#E85A2C] transition-colors text-[11px] uppercase tracking-[0.28em] font-bold"
            >
              <ArrowLeft className="w-4 h-4" /> {tx("Back to All Rounder")} </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="bbq-page"
      className="relative min-h-screen bg-[#0A0806] text-white overflow-x-clip"
      style={{ fontFamily: "'Noto Sans KR', 'Fraunces', serif" }}
    >
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0A0806]/60 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="bbq-back"
            className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{t.bbq?.back || "Back to All Rounder"}</span>
          </Link>

          <div className="flex items-center gap-3 min-w-0">
            <BbqLangToggle />
            <div className="text-white/90 hidden sm:block">
              <AllRounderLogo variant="horizontal" color="#FAFAFA" />
            </div>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section
        ref={heroRef}
        data-testid="bbq-hero"
        className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden"
      >
        {/* Parallax background image / video */}
        <motion.div
          className="absolute inset-0 -z-10"
          style={{ y: bgY, scale: bgScale }}
        >
          {media.hero_bg_mode === "video" && media.video_id ? (
            <video
              autoPlay
              muted
              loop
              playsInline
              data-testid="bbq-hero-bg-video"
              className="w-full h-full object-cover"
              src={`${API}/bbq-video?v=${encodeURIComponent(media.video_updated_at || "")}`}
              poster={media.hero_image || HERO_IMG}
            />
          ) : (
            <img
              src={media.hero_image || HERO_IMG}
              alt="BBQ party hero"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0806]/40 via-[#0A0806]/50 to-[#0A0806]" />
        </motion.div>

        <motion.div
          style={{ opacity: textOpacity }}
          className="relative z-10 px-5 sm:px-10 pb-14 sm:pb-24 max-w-7xl mx-auto w-full"
        >
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C] flex items-center gap-3"
          >
            <span className="inline-block w-6 h-px bg-[#E85A2C]" />
            {event.eyebrow || t.bbq?.eyebrow || "South Bank Social · Aug 7"}
          </motion.p>

          <KineticTitle />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-3 flex items-center gap-3 text-white/80"
          >
            <span className="text-[#E85A2C] text-2xl sm:text-3xl font-light">×</span>
            <span
              className="text-white/90 text-lg sm:text-2xl font-bold tracking-[0.24em] uppercase"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}
            >
              All Rounder
            </span>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-6 sm:mt-8 max-w-xl text-white/70 text-base sm:text-lg leading-relaxed"
          >
            {t.bbq?.sub_a || "BBQ, drinks, snacks & new friends. Just bring yourself and join the fun!"}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href="#rsvp"
              data-testid="bbq-hero-cta"
              className="group inline-flex items-center gap-2 bg-[#E85A2C] hover:bg-[#c94a20] text-white font-bold text-[15px] h-12 px-6 rounded-sm transition-colors"
            >
              {t.bbq?.cta || "Reserve Your Spot"}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <div className="text-white/50 text-xs uppercase tracking-[0.24em] font-semibold">
              <span data-testid="bbq-count" className="text-[#E85A2C] tabular">
                {count.total}
              </span>{" "}
              / {count.capacity} {t.bbq?.spots || "spots taken"}
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* DETAILS */}
      <section
        data-testid="bbq-details"
        className="relative border-t border-white/5"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-10 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-12">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]"> {tx("// The Details")} </p>
            <h2
              className="mt-4 text-white text-4xl sm:text-5xl font-bold tracking-tight leading-[0.95]"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
            >
              {event.location_area} <br /> {event.location_region}
            </h2>
            <div className="mt-8 aspect-[4/5] max-w-[420px] overflow-hidden rounded-sm border border-white/10">
              <img
                src={media.detail_image || IMG_SKEWERS}
                alt="BBQ event"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <DetailCell
              eyebrow={tx("Date")}
              value={event.date_label}
              subtitle={event.date_note}
              Icon={Calendar}
            />
            <DetailCell
              eyebrow={tx("Time")}
              value={event.time_label}
              subtitle={event.time_note}
              Icon={Clock}
            />
            <DetailCell
              eyebrow={tx("Location")}
              value={event.location_label}
              subtitle={event.location_note}
              Icon={MapPin}
            />
            <DetailCell
              eyebrow={tx("Capacity")}
              value={tx("{total} / {capacity} guests",{total:count.total,capacity:count.capacity})}
              subtitle={tx("Registration closes when full")}
              Icon={Users}
            />
          </Reveal>
        </div>
      </section>

      {/* PROMO VIDEO */}
      {media.video_id && (
        <section
          data-testid="bbq-video-section"
          className="relative border-t border-white/5 bg-[#0F0B08]"
        >
          <div className="max-w-5xl mx-auto px-5 sm:px-10 py-20 sm:py-28">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]"> {tx("// Promo Film")} </p>
              <h2
                className="mt-4 text-white text-4xl sm:text-6xl font-bold tracking-tight leading-[0.95]"
                style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
              > {tx("Feel the vibe.")} </h2>
            </Reveal>
            <Reveal delay={0.15} className="mt-10">
              <div className="rounded-md overflow-hidden border border-white/15 bg-black shadow-2xl">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  data-testid="bbq-promo-video"
                  className="w-full aspect-video object-contain bg-black"
                  src={`${API}/bbq-video?v=${encodeURIComponent(media.video_updated_at || "")}`}
                />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <Marquee title={event.title} />

      {/* RSVP */}
      <section
        id="rsvp"
        data-testid="bbq-rsvp-section"
        className="relative border-t border-white/5 overflow-hidden"
      >
        {/* Semi-transparent background image behind registration text */}
        {media.hero_image && (
          <div aria-hidden className="absolute inset-0 -z-10">
            <img
              src={media.hero_image}
              alt=""
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-[#0A0806]/80" />
          </div>
        )}
        <div className="max-w-4xl mx-auto px-5 sm:px-10 py-20 sm:py-28">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]">
              {t.bbq?.reg || "// Registration"}
            </p>
            <h2
              className="mt-4 text-white text-4xl sm:text-6xl font-bold tracking-tight leading-[0.95]"
              style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
            >
              {t.bbq?.reg_title || "Claim your seat."}
            </h2>
            <p className="mt-4 text-white/60 max-w-lg leading-relaxed">
              {paymentEmergency
                ? tx("현재 카드 결제 오류로 계좌이체로 진행됩니다. 신청 후 안내되는 계좌로 이체해 주세요.")
                : t.bbq?.reg_sub || "Pay securely by card to lock in your spot. Confirmation email lands right after payment."}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10">
            <RsvpForm event={event} paymentEmergency={paymentEmergency} bank={bank} />
          </Reveal>
        </div>
      </section>

      {/* GALLERY CAROUSEL */}
      <GalleryCarousel images={gallery} />

      {/* EVENT REVIEWS */}
      <ReviewsSection />

      {/* FOOTER */}
      <footer className="border-t border-white/5 bg-[#0A0806] py-12 px-5 sm:px-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <AllRounderLogo variant="horizontal" color="#FAFAFA" />
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/40">
            © {new Date().getFullYear()} {tx("All Rounder · South Bank BBQ")} </div>
        </div>
      </footer>
    </main>
  );
}
